/**
 * useSdbDetail — React wrapper around `SdbInqService.fetchBoxDetail`.
 *
 * - Abort-capable: a fresh AbortController per `acctId` change, and
 *   unmount tears down in-flight requests.
 * - Exposes `reload()` for officer-initiated refresh after mutations
 *   (access recorded → last-access echoes, close-out → vacant flips).
 * - Mirrors `useCdDetail` in `modules/RCD/hooks/useCdDetail.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { SdbInqService } from '../services/SdbInqService';
import type { BoxDetail } from '../types';

export interface UseSdbDetailResult {
  data: BoxDetail | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useSdbDetail(acctId: string | undefined): UseSdbDetailResult {
  const client = useApiClient();
  const abortRef = useRef<AbortController | null>(null);
  const [tick, setTick] = useState<number>(0);

  const [data, setData] = useState<BoxDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(!!acctId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!acctId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    // Cancel any in-flight request from a previous `acctId` or reload.
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    const svc = new SdbInqService(client);
    void (async (): Promise<void> => {
      try {
        const detail = await svc.fetchBoxDetail(acctId, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        setData(detail);
        setLoading(false);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load SDB detail.');
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acctId, client, tick]);

  const reload = useCallback((): void => setTick((t) => t + 1), []);

  return { data, loading, error, reload };
}
